import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalHeader } from "reactstrap";

import ExportExcelForm from "containers/Company/components/ExportExcelForm";
import { TooltipComp } from "components/Tooltip";
import ExportExcel from "helpers/exportExcel";
import { stringToMask } from "helpers/string";

import {
	buildBenefitsFilterSearch,
	getBenefits,
	readBenefitsFilters
} from "../../actions";
import BenefitUpdateModal from "../../components/BenefitUpdateModal";
import BigNumbers from "../../components/BigNumbers";
import Filters from "../../components/Filters";
import InsuredUsersTable from "../../components/InsuredUsersTable";
import { EMPTY_FILTERS, formatPartner, getStatusMeta } from "../../constants";

import {
	ErrorMessage,
	ExportButton,
	HeaderRow,
	PageTitle,
	PageWrapper,
	TitleGroup
} from "./styles";

const emptyMetrics = {
	totalProviders: 0,
	lifeInsurance: { active: 0, pending: 0 },
	dit: { active: 0, pending: 0 },
	triggers: 0
};

const toExportData = data => {
	const rows = data.map(item => ({
		Prestador: item.name?.trim() || "",
		CPF: item.cpf ? stringToMask(item.cpf, "###.###.###-##") : "",
		Contratante: item.company?.name || "",
		CNPJ: item.company?.cnpj
			? stringToMask(item.company.cnpj, "##.###.###/####-##")
			: "",
		"Parceiro Seguro de Vida": formatPartner(item.lifeInsurance?.partner),
		"Status Seguro de Vida": getStatusMeta(item.lifeInsurance?.status).label,
		"Parceiro DIT": formatPartner(item.dit?.partner),
		"Status DIT": getStatusMeta(item.dit?.status).label,
		Acionamentos: item.triggers || 0,
		"Última observação": item.lastObservation || ""
	}));

	return {
		columns: rows[0] ? Object.keys(rows[0]) : [],
		data: rows
	};
};

const InsuredUsers = () => {
	const token = useSelector(state => state.login.token);
	const history = useHistory();
	const location = useLocation();
	const initialFilters = readBenefitsFilters(location.search);
	const table = useRef(null);
	const requestId = useRef(0);
	const urlSearch = useRef(location.search);
	const [draftFilters, setDraftFilters] = useState(initialFilters);
	const [appliedFilters, setAppliedFilters] = useState(initialFilters);
	const [items, setItems] = useState([]);
	const [metrics, setMetrics] = useState(emptyMetrics);
	const [count, setCount] = useState(0);
	const [pages, setPages] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [selectedProvider, setSelectedProvider] = useState(null);
	const [reloadVersion, setReloadVersion] = useState(0);
	const [modalExport, setModalExport] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [progress, setProgress] = useState(0);

	useEffect(() => {
		if (urlSearch.current === location.search) return;

		urlSearch.current = location.search;
		const filters = readBenefitsFilters(location.search);
		setDraftFilters(filters);
		setAppliedFilters(filters);
		setReloadVersion(current => current + 1);
	}, [location.search]);

	const handleFetchData = async tableState => {
		if (!tableState) return;

		const { page, pageSize, sorted = [] } = tableState;
		const currentRequest = requestId.current + 1;
		requestId.current = currentRequest;
		setLoading(true);
		setError("");

		try {
			const result = await getBenefits(
				{
					filters: appliedFilters,
					skip: page * pageSize,
					take: pageSize,
					sort: sorted[0]?.id || "name",
					desc: sorted[0]?.desc || false
				},
				token
			);

			if (requestId.current !== currentRequest) return;

			const total = result.count || 0;
			setItems(result.items || []);
			setMetrics(result.metrics || emptyMetrics);
			setCount(total);
			setPages(Math.max(Math.ceil(total / pageSize), 1));
		} catch (requestError) {
			if (requestId.current !== currentRequest) return;
			setItems([]);
			setMetrics(emptyMetrics);
			setCount(0);
			setPages(1);
			setError(requestError.message);
			toast.error(requestError.message);
		} finally {
			if (requestId.current === currentRequest) setLoading(false);
		}
	};

	const handleFilterChange = (field, value) => {
		setDraftFilters(current => ({ ...current, [field]: value }));
	};

	const applyFilters = filters => {
		const search = buildBenefitsFilterSearch(filters, location.search);
		const nextLocationSearch = search ? `?${search}` : "";

		setAppliedFilters({ ...filters });
		setReloadVersion(current => current + 1);

		if (nextLocationSearch !== location.search) {
			urlSearch.current = nextLocationSearch;
			history.push({
				pathname: location.pathname,
				search: nextLocationSearch
			});
		}
	};

	const handleApplyFilters = () => applyFilters(draftFilters);

	const handleClearFilters = () => {
		setDraftFilters({ ...EMPTY_FILTERS });
		applyFilters({ ...EMPTY_FILTERS });
	};

	const toggleExportModal = () => {
		if (exporting) return;
		setModalExport(current => !current);
		setProgress(0);
	};

	const handleExport = async types => {
		if (!types.length) return;

		if (!count) {
			toast.warning("Não há dados para exportar.");
			return;
		}

		setExporting(true);
		setProgress(0);

		try {
			const exportedItems = [];
			const take = 500;

			for (let skip = 0; skip < count; skip += take) {
				const result = await getBenefits(
					{
						filters: appliedFilters,
						skip,
						take,
						sort: "name",
						desc: false
					},
					token
				);

				exportedItems.push(...(result.items || []));
				setProgress(
					Math.min(Math.round((exportedItems.length / count) * 100), 100)
				);
			}

			const exportData = toExportData(exportedItems);
			types.forEach(type => ExportExcel(exportData, "Beneficios", type));
			toast.success("Listagem exportada com sucesso.");
			setModalExport(false);
		} catch (exportError) {
			toast.error(exportError.message);
		} finally {
			setExporting(false);
			setProgress(0);
		}
	};

	const onSubmitExport = data => {
		const types = [];
		if (data.exportExcel) types.push("xlsx");
		if (data.exportCsv) types.push("csv");
		handleExport(types);
	};

	const refreshList = () => setReloadVersion(current => current + 1);

	return (
		<PageWrapper>
			<HeaderRow>
				<TitleGroup>
					<PageTitle>Benefícios</PageTitle>
					<TooltipComp
						id="benefits-page-title-tooltip"
						message="Somente Contratantes e Prestadores ativos no sistema"
					/>
				</TitleGroup>
				<ExportButton
					type="button"
					onClick={toggleExportModal}
					disabled={loading || exporting}
				>
					Exportar listagem
				</ExportButton>
			</HeaderRow>

			<Filters
				filters={draftFilters}
				onChange={handleFilterChange}
				onApply={handleApplyFilters}
				onClear={handleClearFilters}
				loading={loading}
			/>
			<BigNumbers metrics={metrics} loading={loading} />
			{error && <ErrorMessage role="alert">{error}</ErrorMessage>}
			<InsuredUsersTable
				key={reloadVersion}
				tableRef={table}
				items={items}
				count={count}
				pages={pages}
				loading={loading}
				onFetchData={handleFetchData}
				onOpenDetails={setSelectedProvider}
			/>

			<BenefitUpdateModal
				open={Boolean(selectedProvider)}
				provider={selectedProvider}
				token={token}
				onClose={() => setSelectedProvider(null)}
				onUpdated={refreshList}
			/>

			<Modal isOpen={modalExport} toggle={toggleExportModal}>
				<ModalHeader
					toggle={toggleExportModal}
					className="card__title bold-text"
				>
					Exportar Relatório
				</ModalHeader>
				<ModalBody className="theme-light">
					<ExportExcelForm
						onSubmit={onSubmitExport}
						progress={progress}
						disabled={exporting}
					/>
				</ModalBody>
			</Modal>
		</PageWrapper>
	);
};

export default InsuredUsers;
